import React from "react";
import { Link } from "react-router-dom";
import {
  Users, UserCheck, ClipboardCheck, Trophy, GraduationCap,
  Sigma, Atom, FlaskConical, Leaf, ScrollText, Globe2, LineChart,
  Landmark, BookOpen, Languages, BookType, BookMarked,
  ArrowRight, Star, CheckCircle2, Award, Clock, MapPin
} from "lucide-react";
import { INSTITUTE, PROGRAMS, WHY_US, STATS, TOPPER_CATEGORIES } from "@/lib/content";
import useSEO from "@/hooks/useSEO";
import HeroBannerSlider from "@/components/HeroBannerSlider";

const ICONS = {
  Users, UserCheck, ClipboardCheck, Trophy, GraduationCap,
  Sigma, Atom, FlaskConical, Leaf, ScrollText, Globe2, LineChart,
  Landmark, BookOpen, Languages, BookType, BookMarked,
};

export default function Home() {
  useSEO({
    title: "Best JEE, NEET & Foundation Coaching in Jalgaon | Kothari Classes",
    description: "Kothari Classes - Jalgaon's most trusted coaching institute for JEE, NEET & foundation courses (8th–12th). Personal attention, expert faculty, and strong academic results.",
    canonical: "https://www.kothariclasses.in/",
  });
  return (
    <div data-testid="home-page">
      <HeroBannerSlider />

      {/* HERO */}
      <section className="relative overflow-hidden bg-white" data-testid="hero-section">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-32 -right-32 w-[520px] h-[520px] bg-red-600/5 rounded-full blur-3xl" />
          <div className="absolute top-40 left-1/3 w-[320px] h-[320px] bg-red-600/10 rounded-full blur-3xl" />
          <img
            src={INSTITUTE.logo_symbol}
            alt=""
            aria-hidden="true"
            className="absolute -right-20 top-1/2 -translate-y-1/2 w-[600px] opacity-[0.04] select-none pointer-events-none hidden lg:block"
          />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 lg:pt-20 pb-20 lg:pb-28 relative">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7 space-y-8 fade-up">
              <div className="flex items-center gap-3">
                <span className="w-10 h-px bg-red-600" />
                <span className="eyebrow">Admissions Open · 2026–27</span>
              </div>

              <div className="space-y-4">
                <p className="font-brand text-base sm:text-lg font-semibold tracking-[0.05em] text-red-600 uppercase" data-testid="hero-brandline">
                  Kothari Classes — Jalgaon's Most Trusted Coaching Institute
                </p>
                <h1 className="font-display text-4xl sm:text-5xl lg:text-[5.2rem] font-black tracking-[-0.03em] leading-[0.98] text-gray-950">
                  Best{" "}
                  <span className="relative inline-block">
                    <span className="relative z-10">IIT-JEE, NEET</span>
                    <span className="absolute left-0 right-0 h-3 bg-red-600/90 -z-0" style={{bottom: '6px'}}></span>
                  </span>{" "}
                  &amp; Foundation<br/>
                  Coaching in <span className="italic font-semibold text-red-600">Jalgaon.</span>
                </h1>
              </div>

              <p className="font-brand text-sm font-semibold uppercase tracking-[0.18em] text-gray-700">
                35+ Years of Excellence in Education
              </p>

              <p className="text-lg text-gray-600 max-w-xl leading-relaxed">
                Top <strong>IIT-JEE coaching in Jalgaon</strong>, the best <strong>NEET classes in Jalgaon</strong> and structured <strong>MHT-CET classes in Jalgaon</strong> — plus <strong>10th coaching classes in Jalgaon</strong> and <strong>Foundation courses</strong> from 8th. Small batches, expert faculty, strong results.
              </p>

              <div className="flex flex-wrap gap-3">
                <Link to="/admissions" className="btn-primary rounded-none text-sm" data-testid="hero-enroll-button">
                  Enroll Now <ArrowRight className="w-4 h-4" />
                </Link>
                <Link to="/contact" className="btn-secondary rounded-none text-sm" data-testid="hero-contact-button">
                  Contact Us
                </Link>
              </div>

              <div className="flex items-center gap-6 pt-4 border-t border-gray-200">
                <div className="flex -space-x-2">
                  {[
                    "https://images.pexels.com/photos/3184631/pexels-photo-3184631.jpeg?auto=compress&cs=tinysrgb&w=80&h=80&fit=crop",
                    "https://images.pexels.com/photos/8364020/pexels-photo-8364020.jpeg?auto=compress&cs=tinysrgb&w=80&h=80&fit=crop",
                    "https://images.pexels.com/photos/3231359/pexels-photo-3231359.jpeg?auto=compress&cs=tinysrgb&w=80&h=80&fit=crop",
                  ].map((src, i) => (
                    <img key={i} src={src} alt="" className="w-10 h-10 rounded-full border-2 border-white object-cover" />
                  ))}
                </div>
                <div className="text-sm">
                  <div className="flex items-center gap-1">
                    {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 fill-red-600 text-red-600" />)}
                  </div>
                  <div className="text-gray-600">Trusted by <span className="font-semibold text-gray-900">10,000+</span> families in Jalgaon</div>
                </div>
              </div>
            </div>

            {/* Visual */}
            <div className="lg:col-span-5 relative fade-up" style={{ animationDelay: "120ms" }}>
              <div className="relative">
                <div className="absolute -top-4 -left-4 w-full h-full border-2 border-red-600" />
                <img
                  src="/assets/hero-image.png"
                  alt="Kothari Classes faculty teaching Biology — Cell Organelles, Mitochondria & Ribosome"
                  loading="eager"
                  className="relative w-full h-[460px] lg:h-[560px] object-cover object-center"
                />
                {/* Floating stat card */}
                <div className="absolute -bottom-6 -left-6 bg-white border border-gray-200 p-5 shadow-xl w-60">
                  <div className="eyebrow mb-1">Board Results</div>
                  <div className="font-display text-4xl font-black text-gray-950">95%+</div>
                  <div className="text-xs text-gray-500 mt-1">across CBSE, ICSE & State Board 2024</div>
                </div>
                <div className="absolute -top-6 -right-6 bg-red-600 text-white p-4 shadow-xl">
                  <Award className="w-5 h-5 mb-1" />
                  <div className="text-xs font-bold tracking-wider uppercase">35+ Yrs</div>
                  <div className="text-[10px] opacity-90">of excellence</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Board marquee stripe */}
        <div className="bg-gray-950 text-white py-4 overflow-hidden border-y-2 border-red-600 relative stripe-red">
          <div className="flex marquee-track gap-16 whitespace-nowrap">
            {[...Array(2)].map((_, ri) => (
              <div key={ri} className="flex gap-16 items-center shrink-0">
                {["CBSE", "ICSE", "STATE BOARD", "NEET", "IIT-JEE", "MHT-CET", "FOUNDATION", "BOARDS 10th & 12th", "4th TO 12th"].map((t) => (
                  <span key={t} className="flex items-center gap-4 font-display font-black text-lg tracking-wider">
                    <span>{t}</span>
                    <span className="w-1.5 h-1.5 bg-red-600 rotate-45" />
                  </span>
                ))}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="bg-white border-b border-gray-200" data-testid="stats-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 flex items-center justify-center text-center">
          <p className="font-display text-lg sm:text-xl lg:text-2xl font-bold tracking-tight text-gray-950" data-testid="promise-tagline">
            <span className="text-red-600">"</span>Building Future Toppers with <span className="text-red-600">Excellence</span>, <span className="text-red-600">Discipline</span> &amp; <span className="text-red-600">Results</span>.<span className="text-red-600">"</span>
          </p>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12 grid grid-cols-2 lg:grid-cols-4 gap-6 border-t border-gray-200 pt-10">
          {STATS.map((s, i) => (
            <div key={s.label} className="py-4 border-l-2 border-red-600 pl-5" data-testid={`stat-card-${i}`}>
              <div className="font-display text-4xl lg:text-5xl font-black text-gray-950 tracking-tighter">{s.value}</div>
              <div className="text-xs lg:text-sm text-gray-500 mt-2 uppercase tracking-widest">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* PROGRAMS */}
      <section className="bg-gray-50 py-20 lg:py-28" data-testid="programs-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-14">
            <div className="max-w-2xl">
              <span className="eyebrow">What we teach</span>
              <h2 className="font-display mt-3 text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight text-gray-950">
                Programs for every stage of the journey.
              </h2>
            </div>
            <p className="text-gray-600 max-w-md">From early learners to entrance aspirants — a structured curriculum for each section, taught by faculty who genuinely care.</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {PROGRAMS.map((p) => (
              <article
                key={p.key}
                data-testid={`program-card-${p.key}`}
                className="group relative bg-white border border-gray-200 card-lift overflow-hidden flex flex-col"
              >
                <div className="relative h-52 overflow-hidden">
                  <img src={p.image} alt={p.label} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  <div className="absolute top-3 left-3 bg-white text-red-600 text-xs font-black px-2.5 py-1 tracking-widest">{p.index}</div>
                </div>
                <div className="p-6 flex-1 flex flex-col">
                  <div className="eyebrow">{p.range}</div>
                  <h3 className="font-display text-2xl font-bold text-gray-950 mt-2">{p.label}</h3>
                  <p className="text-sm text-gray-600 mt-2 leading-relaxed flex-1">{p.description}</p>
                  <div className="flex flex-wrap gap-1.5 mt-4">
                    {p.highlights.map((h) => (
                      <span key={h} className="text-[11px] font-semibold text-gray-700 border border-gray-300 px-2 py-1">{h}</span>
                    ))}
                  </div>
                </div>
                <Link
                  to="/courses"
                  className="absolute inset-0"
                  aria-label={`Learn more about ${p.label}`}
                  data-testid={`program-link-${p.key}`}
                />
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ALL SUBJECT MASTERY */}
      <section className="bg-white py-20 lg:py-28 border-t border-gray-100" data-testid="subject-mastery-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-5">
              <span className="eyebrow">All Subjects · One Roof</span>
              <h2 className="font-display mt-3 text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight text-gray-950 leading-[1]">
                All Subject Mastery<br/>Under One Roof.
              </h2>
              <p className="mt-5 text-gray-600 text-lg max-w-md leading-relaxed">
                We provide complete subject coverage so students don't need multiple classes.
              </p>
              <div className="mt-7 border-l-4 border-red-600 pl-5 py-2">
                <p className="font-display text-lg lg:text-xl font-semibold text-gray-950 leading-snug" data-testid="subject-mastery-hook">
                  Why join 3 different classes when <span className="text-red-600">Kothari Classes</span> provides 360° support for all subjects under one roof?
                </p>
              </div>
            </div>

            <div className="lg:col-span-7 grid sm:grid-cols-2 gap-px bg-gray-200 border border-gray-200">
              {[
                { t: "Maths & Science", sub: "10th CBSE Jalgaon", d: "Best Maths & Science coaching for 10th CBSE in Jalgaon — concept-first teaching with weekly tests.", icon: "Sigma" },
                { t: "English & SST", sub: "SSC Board", d: "Structured English & SST coaching for SSC Board students — grammar, writing skills and analytical depth.", icon: "BookOpen" },
                { t: "Sanskrit", sub: "SSC Board", d: "Dedicated Sanskrit coaching for SSC Board — grammar, translation and high-scoring exam strategies.", icon: "BookMarked" },
                { t: "Foundation Science", sub: "8th – 10th", d: "Maths · Physics · Chemistry · Biology — early IIT-JEE & NEET aligned mastery from 8th onwards.", icon: "Atom" },
              ].map((s, i) => {
                const Icon = ICONS[s.icon] || BookOpen;
                return (
                  <div key={s.t} className="bg-white p-6 lg:p-7 card-lift" data-testid={`subject-mastery-card-${i}`}>
                    <Icon className="w-6 h-6 text-red-600 mb-4" />
                    <h3 className="font-display text-lg font-bold text-gray-950 leading-tight">{s.t}</h3>
                    <div className="text-[11px] uppercase tracking-widest text-gray-500 mt-1">{s.sub}</div>
                    <p className="text-sm text-gray-600 mt-3 leading-relaxed">{s.d}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* WHY CHOOSE US */}
      <section className="bg-gray-950 text-white py-20 lg:py-28 relative overflow-hidden" data-testid="why-us-section">
        <div className="absolute top-0 right-0 w-1/2 h-full opacity-[0.04]" style={{backgroundImage:"radial-gradient(circle at 20% 30%, #fff 1px, transparent 1px)", backgroundSize:"32px 32px"}}/>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="grid lg:grid-cols-12 gap-10">
            <div className="lg:col-span-4">
              <span className="text-xs font-bold tracking-[0.22em] text-red-500 uppercase">Why Kothari</span>
              <h2 className="font-display mt-3 text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight text-white">
                Built on attention, not volume.
              </h2>
              <p className="mt-5 text-gray-400 leading-relaxed">
                We keep batches small on purpose. Every student is seen, challenged and supported — that's why our results speak.
              </p>
              <Link to="/courses" className="inline-flex items-center gap-2 text-sm font-semibold text-red-500 mt-6 hover:text-white transition-colors" data-testid="why-us-cta">
                Explore our approach <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="lg:col-span-8 grid sm:grid-cols-2 gap-px bg-white/10 border border-white/10">
              {WHY_US.map((w, i) => {
                const Icon = ICONS[w.icon] || Users;
                return (
                  <div key={w.title} className="bg-gray-950 p-6 lg:p-8 hover:bg-red-600 transition-colors group" data-testid={`why-us-item-${i}`}>
                    <Icon className="w-7 h-7 text-red-500 group-hover:text-white mb-4" />
                    <h3 className="font-display text-xl font-bold text-white">{w.title}</h3>
                    <p className="text-sm text-gray-400 group-hover:text-white/90 mt-2 leading-relaxed">{w.desc}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* COMPARISON: WHY KOTHARI BEATS NATIONAL CHAINS */}
      <section className="bg-white py-20 lg:py-28" data-testid="comparison-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-12">
            <span className="eyebrow">Local vs National</span>
            <h2 className="font-display mt-3 text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight text-gray-950 leading-[1]">
              Why Kothari Classes<br/>beats National Chains.
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-px bg-gray-200 border border-gray-200">
            {[
              { t: "Batch Size", k: "30 – 40 students", v: "100+ students", icon: "Users" },
              { t: "Mentorship", k: "Direct faculty guidance", v: "Corporate system", icon: "UserCheck" },
              { t: "Experience", k: "36+ years Jalgaon expertise", v: "Generic approach", icon: "Trophy" },
            ].map((c, i) => {
              const Icon = ICONS[c.icon] || Users;
              return (
                <div key={c.t} className="bg-white p-7 lg:p-8" data-testid={`comparison-card-${i}`}>
                  <div className="flex items-center gap-3 mb-5">
                    <div className="w-9 h-9 border border-red-200 bg-red-50 flex items-center justify-center text-red-600">
                      <Icon className="w-4 h-4" />
                    </div>
                    <span className="text-[10px] font-black tracking-[0.22em] uppercase text-gray-500">{c.t}</span>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-start gap-3 border border-red-600 bg-red-50/50 p-3">
                      <div className="text-[10px] font-black tracking-widest text-red-600 mt-0.5">KOTHARI</div>
                      <div className="font-display text-base font-bold text-gray-950 leading-tight">{c.k}</div>
                    </div>
                    <div className="flex items-start gap-3 border border-gray-200 p-3">
                      <div className="text-[10px] font-black tracking-widest text-gray-400 mt-0.5">CHAINS</div>
                      <div className="text-sm text-gray-500 line-through leading-tight">{c.v}</div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="mt-8 border-l-4 border-red-600 bg-gray-50 px-6 py-5" data-testid="comparison-tagline">
            <p className="font-display text-lg lg:text-xl font-semibold text-gray-950 leading-snug">
              <span className="text-red-600">36 Years of Jalgaon's Best Results</span>, now backed by the most intensive <span className="text-red-600">500+ Test Series</span>.
            </p>
          </div>
        </div>
      </section>

      {/* POWER STATEMENT */}
      <section className="bg-gray-950 text-white py-20 lg:py-24 relative overflow-hidden" data-testid="power-statement-section">
        <div className="absolute inset-0 opacity-[0.05]" style={{backgroundImage:"radial-gradient(circle at 20% 30%, #fff 1px, transparent 1px)", backgroundSize:"32px 32px"}}/>
        <div className="absolute -bottom-32 -right-32 w-[520px] h-[520px] bg-red-600/20 rounded-full blur-3xl pointer-events-none" />
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative text-center">
          <span className="text-xs font-bold tracking-[0.22em] text-red-500 uppercase">The Kothari Standard</span>
          <h2 className="font-display mt-5 text-3xl sm:text-5xl lg:text-6xl font-black tracking-[-0.02em] text-white leading-[1.05]" data-testid="power-statement-headline">
            Don't just study for the Boards —<br/>
            <span className="text-red-500 italic">Master them.</span>
          </h2>
          <p className="mt-6 text-lg lg:text-xl text-gray-300 leading-relaxed max-w-3xl mx-auto" data-testid="power-statement-sub">
            Experience Jalgaon's most rigorous <span className="text-white font-semibold">500+ Test Series</span>. We prepare you so you never see an unfamiliar question on exam day.
          </p>
          <div className="flex flex-wrap gap-3 justify-center mt-10">
            <Link to="/admissions" className="bg-white text-red-600 font-semibold px-6 py-3.5 hover:bg-gray-100 transition-colors inline-flex items-center gap-2 text-sm" data-testid="power-cta-enroll">
              Enroll Now <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/resources" className="border border-white text-white font-semibold px-6 py-3.5 hover:bg-white hover:text-gray-950 transition-colors inline-flex items-center gap-2 text-sm" data-testid="power-cta-resources">
              Open Resource Center
            </Link>
          </div>
        </div>
      </section>

      {/* KOTHARIAN PROMISE */}
      <section className="bg-white py-20 lg:py-28 relative overflow-hidden" data-testid="kotharian-promise-section">
        <div className="absolute -top-10 -left-10 w-[420px] h-[420px] bg-red-600/5 rounded-full blur-3xl pointer-events-none" />
        <img
          src={INSTITUTE.logo_symbol}
          alt=""
          aria-hidden="true"
          className="absolute -right-24 bottom-0 w-[420px] opacity-[0.05] select-none pointer-events-none hidden md:block"
        />
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="grid lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-4 space-y-4">
              <span className="eyebrow">The Kotharian Promise</span>
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight text-gray-950 leading-[1]">
                A message<br/>from our Director.
              </h2>
              <div className="flex items-center gap-4 pt-4 border-t border-gray-200 mt-6">
                <img
                  src={INSTITUTE.director.photo}
                  alt={INSTITUTE.director.name}
                  loading="lazy"
                  className="w-16 h-20 object-cover border border-red-200"
                  style={{ objectPosition: "50% 20%", filter: "brightness(1.05) contrast(1.02)" }}
                  data-testid="director-photo"
                />
                <div>
                  <div className="font-display font-bold text-gray-950 text-base">{INSTITUTE.director.name}</div>
                  <div className="text-xs uppercase tracking-widest text-red-600">{INSTITUTE.director.role}</div>
                </div>
              </div>
            </div>

            <div className="lg:col-span-8">
              <div className="relative border-l-4 border-red-600 pl-6 lg:pl-10 py-2">
                <svg className="absolute -top-2 -left-3 w-8 h-8 text-red-600/20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M9.17 6C6.32 6 4 8.32 4 11.17V18h6.83v-6.83H7.5c.17-1.66 1.5-3 3.33-3V6H9.17zm10 0c-2.85 0-5.17 2.32-5.17 5.17V18h6.83v-6.83h-3.33c.17-1.66 1.5-3 3.33-3V6h-1.66z"/>
                </svg>
                <p className="font-display text-xl lg:text-[26px] font-medium leading-[1.45] tracking-tight text-gray-950" data-testid="promise-quote">
                  At Kothari Classes, every child is our child. We do not just coach — we mentor. Our promise is built on <span className="text-red-600 font-bold">personal attention</span> in small batches, <span className="text-red-600 font-bold">concept clarity</span> that never compromises on fundamentals, the <span className="text-red-600 font-bold">discipline</span> and <span className="text-red-600 font-bold">consistency</span> that builds toppers, and a <span className="text-red-600 font-bold">result-oriented</span> classroom where hard work is tracked, celebrated and rewarded. That is the Kotharian way — and it has been for 35+ years.
                </p>
              </div>

              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-gray-200 border border-gray-200 mt-10">
                {[
                  { t: "Personal Mentorship", sub: "Beyond the Classroom Support", d: "We prioritize individual growth over batch sizes. Our faculty provides dedicated one-on-one sessions ensuring personalized guidance for SSC, CBSE, and ICSE students. You are a name with a dream, not just a registration number." },
                  { t: "Foundation Excellence", sub: "NCERT-First Approach", d: "Success in IIT-JEE, NEET, and MHT-CET begins with a strong foundation. We focus on mastering NCERT concepts before advanced preparation." },
                  { t: "Holistic Discipline", sub: "The Bridge to Success", d: "Excellence comes from consistency. Our structured routine includes regular testing, time management training, and a focused environment." },
                  { t: "Proven Results", sub: "Rank-Driven Outcomes", d: "Our results are driven by performance tracking and analysis with consistent top scorers in Board and Competitive exams." },
                ].map((v, i) => (
                  <div key={v.t} className="bg-white p-5 card-lift" data-testid={`promise-pillar-${i}`}>
                    <div className="text-[10px] font-black tracking-[0.22em] text-red-600 uppercase">0{i + 1}</div>
                    <div className="font-display font-bold text-gray-950 mt-2 leading-tight">{v.t}</div>
                    <div className="text-[11px] uppercase tracking-widest text-gray-500 mt-1">{v.sub}</div>
                    <div className="text-xs text-gray-600 mt-2.5 leading-relaxed">{v.d}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* RESULTS / TOPPERS */}
      <section className="bg-white py-20 lg:py-28" data-testid="results-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-12">
            <div>
              <span className="eyebrow">Results for 2025</span>
              <h2 className="font-display mt-3 text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight text-gray-950">
                Results for 2025.
              </h2>
              <p className="mt-4 text-gray-600 max-w-xl">
                {INSTITUTE.promise}
              </p>
            </div>
            <Link to="/admissions" className="btn-secondary rounded-none text-sm self-start" data-testid="results-cta">
              Join the next batch <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="space-y-14">
            {TOPPER_CATEGORIES.map((cat, ci) => (
              <div key={cat.key} data-testid={`topper-category-${cat.key}`}>
                <div className="flex items-end justify-between mb-5 pb-3 border-b border-gray-200">
                  <div className="flex items-center gap-4">
                    <span className="font-display text-xs font-black text-red-600 tracking-[0.3em]">0{ci + 1}</span>
                    <div>
                      <h3 className="font-display text-2xl lg:text-3xl font-black text-gray-950">{cat.title}</h3>
                      <span className="text-xs uppercase tracking-widest text-gray-500">{cat.accent}</span>
                    </div>
                  </div>
                  <span className="hidden sm:inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-gray-500">
                    <Trophy className="w-4 h-4 text-red-600" /> {cat.toppers.length} Achievers
                  </span>
                </div>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {cat.toppers.map((t, i) => (
                    <div key={t.name} className="border border-gray-200 p-5 card-lift relative bg-white" data-testid={`topper-${cat.key}-${i}`}>
                      <div className="flex items-start justify-between">
                        {t.photo ? (
                          <img
                            src={t.photo}
                            alt={t.name}
                            loading="lazy"
                            className="w-16 h-20 object-cover border border-red-200 bg-gray-50"
                            style={{ objectPosition: "50% 18%", filter: "brightness(1.06) contrast(1.02)" }}
                          />
                        ) : (
                          <div className="w-16 h-20 bg-red-600 text-white flex items-center justify-center font-display text-lg font-black">
                            {t.name.split(" ").map((p) => p[0]).slice(0, 2).join("")}
                          </div>
                        )}
                        <span className="text-[10px] font-bold tracking-widest text-gray-500 uppercase">{cat.key === "neet" ? "Medical" : "2025"}</span>
                      </div>
                      <div className="mt-4 font-display text-lg font-bold text-gray-950 leading-tight">{t.name}</div>
                      <div className="mt-3 pt-3 border-t border-gray-200 flex items-baseline justify-between gap-2">
                        <span className="text-[10px] uppercase tracking-widest text-gray-500">{t.unit}</span>
                        <span className="font-display text-xl font-black text-red-600 leading-none">{t.score}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ADMISSIONS BANNER */}
      <section className="relative bg-red-600 text-white overflow-hidden" data-testid="admissions-banner">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{backgroundImage:"radial-gradient(circle at 2px 2px, #fff 1px, transparent 1px)", backgroundSize:"24px 24px"}}/>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24 relative">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-8 space-y-6">
              <div className="flex items-center gap-3">
                <span className="w-10 h-px bg-white/70" />
                <span className="text-xs font-bold tracking-[0.22em] uppercase text-white/80">Limited Seats</span>
              </div>
              <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1]">
                Admissions Open<br/>2026 – 27
              </h2>
              <p className="text-white/90 text-lg max-w-xl">
                Free demo classes available across Kids, Main, Senior and Foundation batches. Reserve your seat today.
              </p>
              <div className="flex flex-wrap gap-3 pt-2">
                <Link to="/admissions" className="bg-white text-red-600 font-semibold px-6 py-3.5 hover:bg-gray-100 transition-colors inline-flex items-center gap-2" data-testid="banner-enroll-btn">
                  Enroll Now <ArrowRight className="w-4 h-4" />
                </Link>
                <a href={`tel:${INSTITUTE.phone_raw}`} className="border border-white text-white font-semibold px-6 py-3.5 hover:bg-white hover:text-red-600 transition-colors inline-flex items-center gap-2" data-testid="banner-call-btn">
                  Call {INSTITUTE.phone}
                </a>
              </div>
            </div>
            <div className="lg:col-span-4 space-y-4">
              {[
                { icon: CheckCircle2, t: "Free Demo Class", d: "Sit in a real class before enrolling." },
                { icon: Clock, t: "Flexible Timings", d: "Morning & evening batches available." },
                { icon: MapPin, t: "2 Branches in Jalgaon", d: "Baliram Peth & Yogeshwar Nagar." },
              ].map((f, i) => (
                <div key={i} className="border border-white/30 bg-white/5 backdrop-blur-sm p-4 flex gap-3" data-testid={`banner-feature-${i}`}>
                  <f.icon className="w-5 h-5 mt-0.5 shrink-0" />
                  <div>
                    <div className="font-semibold">{f.t}</div>
                    <div className="text-sm text-white/80">{f.d}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
